import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { Desk, User } from '../../../shared/types';
import { ExtendedTheme, UserInterfaceUserGroup, Link, Loader } from 'neon-design-system';
import ArticlePreviewList from '../../ui/ArticlePreviewList';
import { createStyles, makeStyles, Grid, Box } from '@material-ui/core';
import Contributor from '../../ui/Contributor';
import { useActivityLogger } from '../../hooks/use-activity-logger';
import ChannelPreviewList from '../../ui/ChannelPreviewList';
import { DESK_PAGE } from '../../../gql/desks';
import { sortArrayByPropertyAlphabetically } from '../../../utils/sorting.utils';
import SectionHeaderSmall from '../../ui/SectionHeaderSmall';

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) => createStyles({
	deskIcon: {
		height: '0px',
		backgroundColor: theme.themeColors.secondary.main,
		borderRadius: '50%',
		minHeight: 64,
		minWidth: 64,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		marginRight: 16,
		'& > svg': {
			fill: theme.palette.common.white,
			width: 28,
			height: 24
		}
	},
	link: {
		fontSize: 'inherit'
	}
}))



const DeskPage = () => {
	const classes = useStyles();
	const { _id } = useParams();

	const { data, loading } = useQuery<{ document: Desk }>(DESK_PAGE, {
		skip: !_id,
		fetchPolicy: 'network-only',
		variables: { id: _id }
	});

	const desk = data?.document;
	const deskName = desk?.name || '';
	const deskEmail = desk?.email || '';

	useActivityLogger('Desk', deskName, _id);
	if (loading && !data) return <Loader />;

	const contributors = sortArrayByPropertyAlphabetically(desk?.contributors || [], 'name');
	const channels = sortArrayByPropertyAlphabetically(desk?.channels || [], 'name');

	return (
		<Grid container spacing={8}>
			<Grid item xs={12}>
				<Box display="flex" alignItems="center" mb={1}>
					<div className={classes.deskIcon}>
						<UserInterfaceUserGroup size="medium" />
					</div>
					<SectionHeaderSmall
						large
						title={deskName}
						subtitle={
							<>
								For general inquiries, please contact
								<Link
									ignoreRouter
									to={`mailto:${deskEmail}`}
									className={classes.link}
								>
									&nbsp;{deskEmail}
								</Link>
							</>
						}
					/>
				</Box>
			</Grid>
			<Grid item xs={12}>
				<SectionHeaderSmall
					title="Team contributors"
					subtitle="All the people that contribute to writing articles"
				/>
				<Grid container spacing={2}>
					{contributors.map((c: User) => (
						<Grid item key={c._id} xs={12} sm={6} md={4} lg={3}>
							<Contributor
								_id={c._id}
								name={c.name}
								jobFunction={c.position}
								phone={c.phone}
							/>
						</Grid>
					))}
				</Grid>
			</Grid>
			<Grid item xs={12}>
				<SectionHeaderSmall
					title={`Channels curated by ${deskName}`}
				/>
				<ChannelPreviewList channels={channels} loading={loading} />
			</Grid>
			<Grid item xs={12}>
				<SectionHeaderSmall
					title={`Articles ${deskName} has contributed to`}
					subtitle="All the articles published by this Desk"
				/>
				<ArticlePreviewList
					deskId={_id}
					source="Desk Page"
				/>
			</Grid>
		</Grid>
	);

};

export default DeskPage;
